import _ from 'lodash';
import { useHttpClient } from '@wix/fe-essentials-standalone';
import { useCallback, useEffect, useState } from 'react';
import { Budget } from '@components/CompactBriefPage/CompactBriefPage';
import { DEFAULT_CURRENCY, DEFAULT_CURRENCY_SYMBOL } from '@utils/constants';
import { getTranslatedCurrencyOptions } from '@components/Header/utils/Currency/formatCurrencyOptions';
import type { ServicePriceData } from '@common/types';

const useBriefBudget = ({
  userCurrency,
  localeDataTranslations,
}: {
  userCurrency: string | undefined;
  localeDataTranslations: any;
}) => {
  const httpClient = useHttpClient();
  const [budget, setBudget] = useState<Budget>({
    currency: userCurrency || DEFAULT_CURRENCY,
    budgetValue: 0,
    conversionRate: 1,
    symbol: DEFAULT_CURRENCY_SYMBOL,
  });
  const [currencyOptionsArr, setCurrencyOptionsArr] = useState([] as any);
  const [servicePriceData, setServicePriceData] = useState<
    ServicePriceData | undefined
  >(undefined);
  const currencyOptions = getTranslatedCurrencyOptions(
    localeDataTranslations!,
    'id',
  );

  useEffect(() => {
    if (budget.currency !== DEFAULT_CURRENCY) {
      handleBudgetChange({ currency: userCurrency });
    }
  }, []);

  useEffect(() => {
    const options = currencyOptions.map((option: any) => {
      const fullCurrencyName = option.label.split(' ');
      const symbol = fullCurrencyName.pop();
      const currency = fullCurrencyName.pop();
      return {
        value: `${currency} ${symbol}`,
        id: option.id,
      };
    });
    setCurrencyOptionsArr(options);
  }, [localeDataTranslations]);

  useEffect(() => {
    if (currencyOptionsArr && userCurrency) {
      const symbolString = _.find(
        currencyOptionsArr,
        (option: any) => option.id === userCurrency,
      );
      const symbol = symbolString
        ? symbolString.value.split(' ')[1].replace('(', '').replace(')', '')
        : DEFAULT_CURRENCY_SYMBOL;
      setBudget({
        ...budget,
        symbol,
      });
    }
  }, [currencyOptionsArr, userCurrency]);

  const getConversionRate = async (currency: string) => {
    const { data: conversionRate } = await httpClient.get<number>(
      `/api/currency/getCurrencyConversionRate?to=${currency}`,
    );
    return conversionRate;
  };

  const updateServicePriceData = async (serviceId: string) => {
    const { data: priceData } = await httpClient.get<
      ServicePriceData | undefined
    >(`/api/getServicePriceData?serviceId=${serviceId}`);
    setServicePriceData(priceData);
  };

  const updateConversionRate = async (currency: string) => {
    const conversionRate = await getConversionRate(currency);
    setBudget((prev) => {
      return {
        ...prev,
        conversionRate,
      };
    });
  };

  const handleBudgetChange = useCallback(
    (val: Partial<Budget>) => {
      if (val.currency) {
        updateConversionRate(val.currency);
        setBudget((prev) => {
          return {
            ...prev,
            currency: val.currency!,
            ...(val?.symbol ? { symbol: val.symbol } : {}),
          };
        });
      } else {
        setBudget((prev) => {
          return {
            ...prev,
            budgetValue: _.isNumber(val.budgetValue) ? val.budgetValue : 0,
          };
        });
      }
    },
    [userCurrency, httpClient, budget.currency, budget.conversionRate],
  );

  return {
    budget,
    servicePriceData,
    currencyOptionsArr,
    updateServicePriceData,
    handleBudgetChange,
  };
};

export default useBriefBudget;
